import { useState, useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { FiUsers, FiCalendar, FiClock, FiCheckCircle, FiTool, FiHash, FiZap, FiActivity, FiMapPin, FiPhone } from 'react-icons/fi'
import { ReactComponent as GeneratorIcon } from '../../../shared/img/icon/generator.svg'
import api from '../../../utils/api'
import AuthImage from '../../../shared/components/AuthImage/AuthImage'
import logo from '../../../shared/img/logo.png'
import { formatCPF, formatCNPJ, formatCEP, formatPhone } from '../../../utils/formatters'
import './ServiceOrderPDFPreview.css'
import { buildChecklistSections } from '../../../utils/checklistSections'
import { getOrderServiceAddress } from '../../../utils/generatorLocation'

// Página de trabalho: mesmo conteúdo que vai pro PDF (generateServiceOrderPDF.js),
// só que renderizado em HTML/CSS pra dar pra ajustar o layout com hot-reload em
// vez de gerar um PDF a cada teste. Quando o layout ficar pronto, vira o modelo
// pra reescrever a geração do PDF de verdade.
function formatDate(value) {
    if (!value) return ''
    const [year, month, day] = value.split('-')
    return day && month && year ? `${day}/${month}/${year}` : value
}

function formatDuration(totalSeconds) {
    const h = Math.floor(totalSeconds / 3600)
    const m = Math.floor((totalSeconds % 3600) / 60)
    if (h === 0) return `${m}min`
    return `${h}h ${m}min`
}

function formatDateTime(value) {
    if (!value) return null
    const d = new Date(value)
    if (isNaN(d.getTime())) return null
    const date = d.toLocaleDateString('pt-BR')
    const time = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    return `${date} ${time}`
}

function formatDateOnly(value) {
    if (!value) return null
    const d = new Date(value)
    if (isNaN(d.getTime())) return null
    return d.toLocaleDateString('pt-BR')
}

const InfoCard = ({ icon: Icon, label, value, wide }) => (
    <div className={`sopp-info-card${wide ? ' sopp-info-card-wide' : ''}`}>
        <span className="sopp-info-label">
            {Icon && <Icon size={11} />}
            {label}
        </span>
        <span className="sopp-info-value">{value || '—'}</span>
    </div>
)

// Monta as linhas de endereço/documento do cliente a partir do snapshot salvo
// na OS (mesmos campos usados no card "Dados do cliente" de FormServiceOrder.js).
function getClientInfo(client, addr) {
    if (!client) return null

    const name = client.personType === 'juridica'
        ? (client.corporateName || client.fantasyName)
        : (client.completeName || client.name)
    const document = client.personType === 'juridica' ? formatCNPJ(client.CNPJ) : formatCPF(client.CPF)
    const documentLabel = client.personType === 'juridica' ? 'CNPJ' : 'CPF'

    // endereço = local do serviço (do gerador ou, se for o mesmo, do cliente), congelado na OS
    const addressLine = [addr.logradouro, addr.number].filter(Boolean).join(', ')

    return {
        name: name || client.label || client.name || '—',
        code: client.code,
        document,
        documentLabel,
        addressLine,
        complement: addr.complement,
        district: addr.district,
        city: addr.city,
        state: addr.state,
        zipCode: formatCEP(addr.zipCode),
        phone: formatPhone(client.phone),
    }
}

function ServiceOrderPDFPreview() {
    const { id } = useParams()
    const [token] = useState(localStorage.getItem('token') || '')
    const [serviceOrder, setServiceOrder] = useState(null)

    useEffect(() => {
        api.get(`/serviceOrder/get/${id}`, { headers: { Authorization: `Bearer ${JSON.parse(token)}` } })
            .then((response) => setServiceOrder(response.data.serviceOrder))
    }, [id, token])

    const checklistSections = useMemo(
        () => buildChecklistSections(serviceOrder?.checklist || []),
        [serviceOrder?.checklist]
    )

    if (!serviceOrder) {
        return <div className="sopp-loading">Carregando ordem de serviço...</div>
    }

    const scheduled = serviceOrder.scheduledDate ? formatDate(serviceOrder.scheduledDate) : null

    const clientInfo = getClientInfo(serviceOrder.client, getOrderServiceAddress(serviceOrder))

    // Início é derivado do fim (filledAt) menos a duração cronometrada — não existe
    // um timestamp de início separado salvo na OS, só o de conclusão do preenchimento.
    const requestedAt = formatDateOnly(serviceOrder.createdAt)
    const executedAt = formatDateTime(serviceOrder.filledAt)
    const startedAt = (serviceOrder.filledAt && typeof serviceOrder.timeSpentSeconds === 'number')
        ? formatDateTime(new Date(serviceOrder.filledAt).getTime() - serviceOrder.timeSpentSeconds * 1000)
        : null
    const duration = typeof serviceOrder.timeSpentSeconds === 'number'
        ? formatDuration(serviceOrder.timeSpentSeconds)
        : null

    // TESTE: duplica as fotos reais até ter +5, só pra visualizar a grade cheia — remover depois
    const realAttachments = serviceOrder.attachments || []
    const testAttachments = realAttachments.length > 0
        ? Array.from({ length: realAttachments.length + 5 }, (_, i) => realAttachments[i % realAttachments.length])
        : []

    return (
        <div className="sopp-wrap">
            <div className="sopp-page">

                {/* ── Cabeçalho ── */}
                <div className="sopp-header">
                    <div className="sopp-header-brand">
                        <img src={logo} alt="BestForce" className="sopp-logo" />
                    </div>
                    <div className="sopp-header-doc">
                        <span className="sopp-doc-title">Ordem de Serviço</span>
                        <span className="sopp-doc-code">#{serviceOrder.code || '—'}</span>
                    </div>
                </div>
                <div className="sopp-header-rule" />

                {clientInfo && (
                    <div className="sopp-client-block">
                        <span className="sopp-client-name">Nome/Razão Social: {clientInfo.name}</span>
                        <div className="sopp-info-grid sopp-client-grid">
                            <InfoCard icon={FiHash} label="Código" value={clientInfo.code} />
                            <InfoCard icon={FiHash} label={clientInfo.documentLabel} value={clientInfo.document} />
                            <InfoCard icon={FiPhone} label="Telefone" value={clientInfo.phone} />
                        </div>
                        <span className="sopp-client-name sopp-service-location-title">Local do serviço</span>
                        <div className="sopp-info-grid sopp-client-grid">
                            <InfoCard icon={FiMapPin} label="Endereço" value={clientInfo.addressLine} />
                            <InfoCard icon={FiMapPin} label="Complemento" value={clientInfo.complement} />
                            <InfoCard icon={FiMapPin} label="Bairro" value={clientInfo.district} />
                            <InfoCard icon={FiMapPin} label="Cidade" value={clientInfo.city} />
                            <InfoCard icon={FiMapPin} label="UF" value={clientInfo.state} />
                            <InfoCard icon={FiMapPin} label="CEP" value={clientInfo.zipCode} />
                        </div>
                    </div>
                )}

                {/* ── Detalhes do atendimento ── */}
                <div className="sopp-section">
                    <div className="sopp-section-title">Detalhes do atendimento</div>
                    <div className="sopp-info-grid">
                        <InfoCard icon={FiTool} label="Tipo de OS" value={serviceOrder.procedure?.name || serviceOrder.procedure?.label} />
                        <InfoCard
                            icon={FiUsers}
                            label="Equipe responsável"
                            value={(serviceOrder.technicians || []).map(t => t.name || t.label).join(', ')}
                            wide
                        />
                        <InfoCard icon={FiCheckCircle} label="Status" value={serviceOrder.status?.label} />
                        <InfoCard icon={FiCalendar} label="Data de solicitação" value={requestedAt} />
                        <InfoCard icon={FiCalendar} label="Agendado" value={scheduled} />
                        <InfoCard icon={FiClock} label="Início" value={startedAt} />
                        <InfoCard icon={FiClock} label="Término" value={executedAt} />
                        <InfoCard icon={FiClock} label="Duração" value={duration} />
                    </div>
                </div>

                {/* ── Gerador ── */}
                <div className="sopp-section">
                    <div className="sopp-section-title">Gerador</div>
                    <div className="sopp-info-grid">
                        <div className="sopp-info-card">
                            <span className="sopp-info-label">
                                <GeneratorIcon className="sopp-info-label-icon" />
                                Nome
                            </span>
                            <span className="sopp-info-value">{serviceOrder.generator?.label || serviceOrder.generator?.name || '—'}</span>
                        </div>
                        <InfoCard icon={FiHash} label="Número de Série" value={serviceOrder.generator?.serialNumber} />
                        <InfoCard icon={FiHash} label="Nº de Patrimônio" value={serviceOrder.generator?.patrimonyNumber} />
                        <InfoCard icon={FiTool} label="Fabricante" value={serviceOrder.generator?.manufacturer} />
                        <InfoCard icon={FiActivity} label="Potência" value={serviceOrder.generator?.power} />
                        <InfoCard icon={FiZap} label="Alternador" value={serviceOrder.generator?.alternator} />
                    </div>
                    {serviceOrder.generator?.description && (
                        <p className="sopp-observation sopp-generator-desc">{serviceOrder.generator.description}</p>
                    )}
                </div>

                {/* ── Observação ── */}
                {serviceOrder.observation && (
                    <div className="sopp-section">
                        <div className="sopp-section-title">Observação do técnico</div>
                        <p className="sopp-observation sopp-observation-noborder">{serviceOrder.observation}</p>
                    </div>
                )}

                {/* ── Anexos ── */}
                {/* TESTE: attachments duplicados só pra ver a grade com mais fotos — remover depois */}
                {testAttachments.length > 0 && (
                    <div className="sopp-section">
                        <div className="sopp-section-title">Anexos</div>
                        <div className="sopp-photo-grid">
                            {testAttachments.map((photo, index) => (
                                <div key={`${photo._id}-${index}`} className="sopp-photo-card">
                                    <AuthImage
                                        className="sopp-photo-img"
                                        src={`/images/serviceOrder/${photo.filename}`}
                                        alt={photo.description || 'Foto'}
                                    />
                                    {photo.description && <p className="sopp-photo-desc">{photo.description}</p>}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* ── Checklist ── */}
                {checklistSections.length > 0 && (
                    <div className="sopp-section">
                        <div className="sopp-section-title">Checklist realizado</div>
                        {checklistSections.map((section) => (
                            <div key={section.groupLabel} className="sopp-checklist-group">
                                <div className="sopp-checklist-group-header">{section.groupLabel}</div>
                                {section.items.map((item) => (
                                    <div key={item._id} className="sopp-checklist-item">
                                        <span className="sopp-checklist-item-name">{item.name}</span>
                                        {item.fillType?.value === 'yesno' ? (
                                            <span className={`sopp-yesno-pill ${item.response === 'Sim' ? 'sopp-yesno-sim' : item.response === 'Não' ? 'sopp-yesno-nao' : ''}`}>
                                                {item.response || '—'}
                                            </span>
                                        ) : (
                                            <span className="sopp-checklist-item-value">
                                                {item.response || '—'}
                                                {item.fillType?.value === 'measurement' && item.unit ? ` ${item.unit}` : ''}
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                )}

                {/* ── Assinatura ── */}
                <div className="sopp-section">
                    <div className="sopp-signature-row">
                        <div className="sopp-signature-col">
                            <div className="sopp-signature-slot">
                                {/* TESTE: valor fixo só pra visualizar — remover depois */}
                                <span className="sopp-signature-value">{serviceOrder.signerRole || 'Síndico'}</span>
                            </div>
                            <div className="sopp-signature-line" />
                            <span className="sopp-signature-caption">Tipo Responsável</span>
                        </div>
                        <div className="sopp-signature-col">
                            <div className="sopp-signature-slot">
                                {/* TESTE: valor fixo só pra visualizar — remover depois */}
                                <span className="sopp-signature-value">{serviceOrder.signerName || 'João da Silva'}</span>
                            </div>
                            <div className="sopp-signature-line" />
                            <span className="sopp-signature-caption">Nome</span>
                        </div>
                        <div className="sopp-signature-col">
                            <div className="sopp-signature-slot">
                                {serviceOrder.signature && (
                                    <AuthImage
                                        className="sopp-signature-img-inline"
                                        src={`/images/serviceOrder/${serviceOrder.signature}`}
                                        alt="Assinatura do cliente"
                                    />
                                )}
                            </div>
                            <div className="sopp-signature-line" />
                            <span className="sopp-signature-caption">Assinatura digital</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ServiceOrderPDFPreview
